import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, MoreHorizontal, Trash2, Check, Archive, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CareerCard } from "./CareerCard";
import { GOAL_CATEGORIES, GOAL_CATEGORY_LABELS, OWNER_TYPES, OWNER_LABELS } from "@/lib/careerConfig";
import { formatDate, cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

export function GoalDialog({ open, onOpenChange, playerId, onChanged }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("performance");
  const [owner, setOwner] = useState("agent");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/players/${playerId}/career-plan/goals`, {
        title: title.trim(), category, owner_type: owner, due_date: due || null, progress: 0,
      });
      toast.success(tfr("Objectif ajouté"));
      onOpenChange(false);
      setTitle(""); setCategory("performance"); setOwner("agent"); setDue("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Impossible d'ajouter l'objectif")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle>Nouvel objectif</DialogTitle>
          <DialogDescription>{tfr("Définissez un objectif de développement suivi dans le plan.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="goal-title">Titre</Label>
            <Input id="goal-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={tfr("Ex. Atteindre 80% de temps de jeu")} data-testid="goal-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{tfr("Catégorie")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="goal-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GOAL_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Responsable</Label>
              <Select value={owner} onValueChange={setOwner}>
                <SelectTrigger data-testid="goal-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OWNER_TYPES.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goal-due">{tfr("Échéance (optionnel)")}</Label>
            <Input id="goal-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="goal-due-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tfr("Annuler")}</Button>
          <Button onClick={save} disabled={saving || !title.trim()} data-testid="goal-save-btn">{tfr("Ajouter l'objectif")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalRow({ g, playerId, onChanged }) {
  const [progress, setProgress] = useState(g.progress || 0);
  const overdue = g.due_date && g.due_date < new Date().toISOString().slice(0, 10) && g.status === "active";

  const patch = async (payload, msg) => {
    try {
      await api.patch(`/players/${playerId}/career-plan/goals/${g.id}`, payload);
      if (msg) toast.success(msg);
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour l'objectif")); }
  };
  const remove = async () => {
    try {
      await api.delete(`/players/${playerId}/career-plan/goals/${g.id}`);
      toast.success(tfr("Objectif supprimé"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible de supprimer l'objectif")); }
  };

  return (
    <li className="rounded-xl border border-border bg-secondary/30 p-3.5" data-testid={`goal-row-${g.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground/95">{g.title}</p>
          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-secondary text-muted-foreground border-border">
              {GOAL_CATEGORY_LABELS[g.category] || "Autre"}
            </Badge>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-secondary text-muted-foreground border-border">
              {OWNER_LABELS[g.owner_type] || "Agent"}
            </Badge>
            {g.due_date && (
              <span className={cn("inline-flex items-center gap-1 text-[11px]", overdue ? "text-danger font-medium" : "text-muted-foreground")}>
                <CalendarDays className="h-3 w-3" aria-hidden="true" />
                {formatDate(g.due_date)}{overdue ? " · En retard" : ""}
              </span>
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
              aria-label={`${tfr("Actions sur l'objectif")} ${g.title}`}
              data-testid={`goal-menu-${g.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => patch({ progress: 100, status: "completed" }, tfr("Objectif terminé 🎉"))} data-testid={`goal-complete-${g.id}`}>
              <Check className="h-3.5 w-3.5 mr-2" /> Marquer terminé
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => patch({ status: "archived" }, tfr("Objectif archivé"))} data-testid={`goal-archive-${g.id}`}>
              <Archive className="h-3.5 w-3.5 mr-2" /> Archiver
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={remove} className="text-danger focus:text-danger" data-testid={`goal-delete-${g.id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <Slider
          value={[progress]}
          min={0}
          max={100}
          step={5}
          onValueChange={([v]) => setProgress(v)}
          onValueCommit={([v]) => patch({ progress: v })}
          className="flex-1"
          aria-label={`Progression de l'objectif ${g.title}`}
          data-testid={`goal-slider-${g.id}`}
        />
        <span className="text-xs font-semibold tabular-nums w-9 text-right" data-testid={`goal-progress-${g.id}`}>{progress}%</span>
      </div>
    </li>
  );
}

export function GoalsCard({ goals, playerId, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  const active = goals.filter((g) => g.status === "active");
  const completed = goals.filter((g) => g.status === "completed");

  return (
    <CareerCard
      icon={Target}
      title={tfr("Objectifs en cours")}
      subtitle={`${active.length} ${active.length > 1 ? tfr("actifs") : tfr("actif")} · ${completed.length} ${completed.length > 1 ? tfr("terminés") : tfr("terminé")}`}
      testid="goals-card"
      right={
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="h-8 gap-1 text-xs" data-testid="goal-add-btn">
          <Plus className="h-3.5 w-3.5" /> Objectif
        </Button>
      }
    >
      {active.length === 0 && completed.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{tfr("Aucun objectif défini. Ajoutez 3 à 5 objectifs de développement.")}</p>
      ) : (
        <>
          <ul className="space-y-3">
            {active.map((g) => <GoalRow key={g.id} g={g} playerId={playerId} onChanged={onChanged} />)}
          </ul>
          {completed.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Terminés ({completed.length})</p>
              <ul className="space-y-1.5">
                {completed.map((g) => (
                  <li key={g.id} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`goal-done-${g.id}`}>
                    <Check className="h-3.5 w-3.5 text-success shrink-0" aria-hidden="true" />
                    <span className="line-through truncate">{g.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      <GoalDialog open={addOpen} onOpenChange={setAddOpen} playerId={playerId} onChanged={onChanged} />
    </CareerCard>
  );
}
